/**
 * AppInstallsTile — real-time app-install proxy for the admin dashboard
 * (Aug 2026, iOS launch). Counts native push registrations (APNs = iPhone,
 * FCM = Android) so the admin sees installs immediately instead of waiting
 * 24-48h for Apple's App Analytics. Includes a 14-day mini sparkline.
 */
import { useEffect, useState } from "react";
import { DeviceMobile } from "@phosphor-icons/react";
import api from "../../lib/api";

export const AppInstallsTile = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/admin/app-installs")
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, []);

  if (err) return null;

  const max = Math.max(1, ...(data?.series || []).map((d) => d.count));

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="app-installs-tile">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
            <DeviceMobile weight="duotone" className="h-5 w-5 text-primary" />
            App installs
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Live count via push registrations — Apple Analytics lags 24–48h
          </p>
        </div>
        {data && (
          <div className="text-right">
            <div className="font-display font-bold text-3xl" data-testid="app-installs-active">
              {data.active}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">active devices</div>
          </div>
        )}
      </div>

      {!data ? (
        <div className="mt-4 h-16 rounded-xl bg-muted animate-pulse" />
      ) : (
        <>
          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl bg-muted/60 py-2">
              <div className="font-display font-bold text-xl" data-testid="app-installs-ios">{data.ios}</div>
              <div className="text-[11px] text-muted-foreground">iPhone</div>
            </div>
            <div className="rounded-xl bg-muted/60 py-2">
              <div className="font-display font-bold text-xl" data-testid="app-installs-android">{data.android}</div>
              <div className="text-[11px] text-muted-foreground">Android</div>
            </div>
            <div className="rounded-xl bg-muted/60 py-2">
              <div className="font-display font-bold text-xl" data-testid="app-installs-new7">{data.new_7d}</div>
              <div className="text-[11px] text-muted-foreground">new this week</div>
            </div>
          </div>
          {data.usage_7d && (
            <div className="mt-3 rounded-xl bg-muted/40 px-3 py-2 flex items-center justify-between text-[12px]" data-testid="platform-usage-split">
              <span className="text-muted-foreground">Visitors (7d)</span>
              <span className="font-semibold tabular-nums">
                web {data.usage_7d.web} · iOS app {data.usage_7d.ios_app} · Android app {data.usage_7d.android_app}
              </span>
            </div>
          )}
          <div className="mt-4 flex items-end gap-1 h-12" aria-label="Installs, last 14 days">
            {data.series.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: ${d.count}`}
                className="flex-1 rounded-t bg-primary/70 min-h-[2px]"
                style={{ height: `${Math.max(4, (d.count / max) * 100)}%`, opacity: d.count === 0 ? 0.18 : 1 }}
              />
            ))}
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>14 days ago</span>
            <span>today</span>
          </div>
        </>
      )}
    </div>
  );
};

export default AppInstallsTile;
